import React, { useEffect } from 'react';

import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import SEO, { schemas } from '../components/SEO';
import './InnerPage.css';

const services = [
  {
    icon: '/icons/services/services_25_ai-security.svg', dark: true,
    title: 'Comprehensive AI Security',
    desc: 'End-to-end OWASP LLM Top 10 evaluation with 122+ test cases across 14 attack vectors. Detect prompt injection, jailbreaking, RAG poisoning, data leakage, and more with enterprise-grade precision.',
    tag: '122+ Tests · 14 Vectors',
  },
  {
    icon: '/icons/services/services_26_quality-scoring.svg',
    title: 'AI Response Quality Scoring',
    desc: 'Score every AI response for accuracy, hallucination, relevance, and completeness using ROUGE, BLEU, Semantic Similarity, and LLM-as-Judge evaluation methods — all in a single run.',
    tag: 'LLM-as-Judge · ROUGE · BLEU',
  },
  {
    icon: '/icons/services/services_27_bias-detection.svg',
    title: 'Bias & Fairness Detection',
    desc: 'Uncover hidden bias across 12 critical dimensions — gender, race, religion, age, disability, geography, tone, and cognitive bias — using multi-prompt comparative evaluation.',
    tag: '12 Bias Dimensions · Multi-Prompt',
  },
  {
    icon: '/icons/services/services_28_cicd-pipeline.svg',
    title: 'CI/CD Pipeline Integration',
    desc: 'Embed evaluations directly into your development workflow. Automatically trigger full test suites on every model update, RAG pipeline change, or deployment.',
    tag: 'CI/CD · RAG · MLOps',
  },
  {
    icon: '/icons/services/services_29_custom-tests.svg',
    title: 'Custom Test Case Builder',
    desc: 'Upload domain-specific test cases via CSV, configure persona profiles, set scoring thresholds, and chain evaluations for fully tailored AI pipeline testing.',
    tag: 'CSV Upload · Custom Personas',
  },
  {
    icon: '/icons/services/services_30_compliance.svg',
    title: 'Compliance Report Generation',
    desc: 'Produce audit-ready, OWASP LLM Top 10-mapped PDF reports with severity ratings, score visualisations, remediation guidance, and executive summaries.',
    tag: 'PDF Export · OWASP Map · Audit-Ready',
  },
];

const industries = [
  { icon:'/icons/services/industry_01_healthcare.svg',      title:'Healthcare & Life Sciences', regs:'HIPAA · FDA · SOC 2', text:'Validate medical AI assistants against safety, accuracy, and regulatory compliance. Ensure AI responses don\'t fabricate clinical information or leak patient data.' },
  { icon:'/icons/services/industry_02_financial.svg',       title:'Financial Services',          regs:'SOX · FINRA · GDPR',  text:'Test AI-powered customer service bots for accuracy in financial advice, resistance to social engineering, and compliance with regulatory guidelines.' },
  { icon:'/icons/services/industry_03_engineering.svg',     title:'AI Engineering Teams',        regs:'CI/CD · RAG · MLOps', text:'Integrate security evaluation into your CI/CD pipeline. Run regression tests after every RAG pipeline update to catch quality regressions early.' },
  { icon:'/icons/services/industry_04_risk-compliance.svg', title:'Risk & Compliance',           regs:'OWASP · ISO · NIST',  text:'Generate OWASP-mapped assessment reports for internal audits, board presentations, and regulatory filings. Track security posture across releases.' },
];

export default function ServicesPage() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); obs.unobserve(e.target); } }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <SEO
        title="Services"
        description="Accugen services: AI quality scoring, security testing, bias detection, CI/CD integration, custom test cases, and OWASP-mapped compliance reports for enterprise AI governance."
        canonical="/services"
        keywords="AI evaluation services, LLM security audit, bias detection service, CI/CD AI testing, PDF compliance report"
        schema={schemas.breadcrumb([{name:'Home',path:'/'},{name:'Services',path:'/services'}])}
      />
      <Header />

      <PageHero
        eyebrow="Enterprise"
        title="Built for Enterprise<br/><em>AI Governance</em>"
        subtitle="Accugen serves teams across the AI lifecycle — from development to compliance — with a full suite of evaluation and reporting services."
        crumb="Services"
      />

      {/* Services grid */}
      <section className="ip-section">
        <div className="container">
          <div className="eyebrow reveal" style={{textAlign:'center'}}>What We Offer</div>
          <h2 className="section-h reveal d1" style={{textAlign:'center'}}>All Your AI Evaluation Needs, <em>Covered</em></h2>
          <div className="svc-grid-page">
            {services.map((s, i) => (
              <div key={s.title} className={`svc-card-page${s.dark ? ' svc-dark' : ''} reveal d${(i % 3) + 1}`}>
                <div className={`svc-icon-page${s.dark ? ' svc-icon-dk' : ' svc-icon-lt'}`}><img src={s.icon} alt={s.title} /></div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <span className={s.dark ? 'svc-tag' : 'svc-tag-blue'}>{s.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry focus */}
      <section className="ip-section ip-bg-lav">
        <div className="container">
          <div className="eyebrow reveal" style={{textAlign:'center'}}>Industry Focus</div>
          <h2 className="section-h reveal d1" style={{textAlign:'center'}}>Tailored for Your <em>Industry</em></h2>
          <div className="ip-card-grid2 reveal d2">
            {industries.map(ind => (
              <div key={ind.title} className="ip-industry-card">
                <div className="ip-ind-header">
                  <div className="ip-ind-icon"><img src={ind.icon} alt={ind.title} /></div>
                  <div>
                    <h3>{ind.title}</h3>
                    <div className="ip-ind-regs">{ind.regs}</div>
                  </div>
                </div>
                <p>{ind.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ip-cta-band">
        <div className="container">
          <h2>Ready to Get Started?</h2>
          <p>Talk to our team about the right service package for your organisation.</p>
          <div className="ip-cta-btns">
            <a href="/contact" className="btn-navy" target="_blank" rel="noreferrer">Contact Us →</a>
            <Link to="/contact" className="btn-outline-navy">Contact Sales</Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
